import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import LoadingOverlay from "../components/LoadingOverlay";

function unwrapPresignPayload(d) {
  if (!d || typeof d !== "object") return null;

  const nested =
    d.upload && typeof d.upload === "object"
      ? d.upload
      : d.data?.upload && typeof d.data.upload === "object"
        ? d.data.upload
        : null;

  if (nested?.uploadUrl && (nested?.key || nested?.s3Key)) {
    return { ...d, ...nested, key: nested.key || nested.s3Key };
  }
  if (d.uploadUrl && (d.key || d.s3Key)) {
    return { ...d, key: d.key || d.s3Key };
  }
  if (d.data && typeof d.data === "object" && d.data.uploadUrl) {
    return { ...d, ...d.data, key: d.data.key || d.data.s3Key };
  }
  return d;
}

const pickDocuments = (data) => {
  if (!data || typeof data !== "object") return [];
  const candidates = [
    data.documents,
    data.byelaws,
    data.byeLaws,
    data.items,
    data.list,
    data.data,
    data.result
  ];
  for (const list of candidates) {
    if (Array.isArray(list)) return list;
  }
  if (data.document && typeof data.document === "object") return [data.document];
  return [];
};

const getDocId = (row) => row?.id || row?._id || "";

const getFileName = (row) =>
  String(row?.documentFileName || row?.fileName || row?.name || "").trim();

const formatDateTime = (iso) => {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const statusBadgeStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") {
    return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  }
  if (s === "inactive") {
    return { background: "rgba(239,68,68,0.18)", color: "#dc2626" };
  }
  return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
};

const CONTENT_TYPE_BY_EXT = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp"
};

const ALLOWED_EXTENSIONS = Object.keys(CONTENT_TYPE_BY_EXT);
const FILE_ACCEPT = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".png",
  ".jpeg",
  ".jpg",
  ".webp",
  ...new Set(Object.values(CONTENT_TYPE_BY_EXT))
].join(",");

const fileExtension = (name) => {
  const match = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : "";
};

const isAllowedUpload = (file) => ALLOWED_EXTENSIONS.includes(fileExtension(file?.name));

const contentTypeFor = (file) => {
  const fromBrowser = String(file?.type || "").trim();
  if (fromBrowser && fromBrowser !== "application/octet-stream") return fromBrowser;
  return CONTENT_TYPE_BY_EXT[fileExtension(file?.name)] || "application/octet-stream";
};

const isImageName = (name) => ["png", "jpg", "jpeg", "webp"].includes(fileExtension(name));
const isPdfName = (name) => fileExtension(name) === "pdf";
const isOfficeName = (name) => ["doc", "docx", "xls", "xlsx"].includes(fileExtension(name));

const officeViewerUrl = (url) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

const fileIconClass = (name) => {
  const ext = fileExtension(name);
  if (ext === "pdf") return "fa-solid fa-file-pdf";
  if (ext === "doc" || ext === "docx") return "fa-solid fa-file-word";
  if (ext === "xls" || ext === "xlsx") return "fa-solid fa-file-excel";
  if (isImageName(name)) return "fa-solid fa-file-image";
  return "fa-solid fa-file";
};

const DocumentPreview = ({ name, url, frameClassName }) => {
  if (isImageName(name)) {
    return (
      <div className={`${frameClassName} bylaw-preview-image-wrap`}>
        <img src={url} alt={name} className="bylaw-preview-image" />
      </div>
    );
  }
  if (isPdfName(name) || isOfficeName(name)) {
    return (
      <iframe
        title={name}
        src={isOfficeName(name) ? officeViewerUrl(url) : url}
        className={frameClassName}
      />
    );
  }
  return (
    <div className="text-center" style={{ padding: "32px 16px" }}>
      <i className={fileIconClass(name)} style={{ fontSize: 36, color: "#0d5c66" }} />
      <p className="text-muted" style={{ marginTop: 12 }}>
        Open this file to view it.
      </p>
      <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
        Open document
      </a>
    </div>
  );
};

const DocumentManagement = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);
  const isAdmin = useMemo(() => {
    try {
      return String(localStorage.getItem("role") || "").toUpperCase().trim() === "ADMIN";
    } catch {
      return false;
    }
  }, []);

  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [listReady, setListReady] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [inlineView, setInlineView] = useState(null);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [modal, setModal] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const filteredDocuments = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter((row) => getFileName(row).toLowerCase().includes(query));
  }, [documents, searchInput]);

  const fetchDocuments = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      setListReady(true);
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      setListReady(true);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "byelaw.getAll",
        userId
      });
      const data = response?.data;
      if (data?.status === "success" || data?.success === true) {
        setDocuments(pickDocuments(data));
      } else {
        toast.error(data?.message || "Failed to load bye-law documents");
        setDocuments([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
      setDocuments([]);
    } finally {
      if (didIncrement) loaderDecrement();
      setListReady(true);
    }
  }, [API_BASE_URL, userId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFileChange = (e) => {
    const next = e?.target?.files?.[0] || null;
    if (!next) {
      setFile(null);
      return;
    }
    if (!isAllowedUpload(next)) {
      toast.error("Upload a PDF, Word, Excel, PNG, JPEG, JPG, or WEBP file");
      clearFile();
      return;
    }
    setFile(next);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }
    if (!file) {
      toast.error("Please choose a file");
      return;
    }
    if (!isAllowedUpload(file)) {
      toast.error("Upload a PDF, Word, Excel, PNG, JPEG, JPG, or WEBP file");
      return;
    }

    const fileName = file.name || "document";
    const contentType = contentTypeFor(file);

    setUploading(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;

      const presignRes = await axios.post(`${API_BASE_URL}`, {
        action: "byelaw.getUploadUrl",
        userId,
        fileName,
        contentType
      });
      const presignRaw = presignRes?.data;
      const okPresign =
        presignRaw?.status === "success" ||
        presignRaw?.success === true ||
        !!presignRaw?.uploadUrl ||
        !!presignRaw?.data?.uploadUrl;

      if (!okPresign) {
        throw new Error(presignRaw?.message || "Could not get upload URL");
      }

      const presign = unwrapPresignPayload(presignRaw);
      const uploadUrl = presign?.uploadUrl;
      const key = presign?.key;
      if (!uploadUrl || !key) {
        throw new Error("Invalid upload URL response (missing uploadUrl/key).");
      }

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": presign.contentType || contentType },
        body: file
      });
      if (!putRes.ok) {
        const errText = await putRes.text().catch(() => "");
        throw new Error(
          `File upload failed (${putRes.status}). ${
            errText ? errText.slice(0, 180) : "Check bucket CORS and Content-Type."
          }`
        );
      }

      const confirmRes = await axios.post(`${API_BASE_URL}`, {
        action: "byelaw.confirm",
        userId,
        key,
        fileName
      });
      const confirmData = confirmRes?.data;
      const okConfirm =
        confirmData?.success === true || confirmData?.status === "success";
      if (!okConfirm) {
        throw new Error(confirmData?.message || "Could not save the document");
      }

      toast.success(confirmData?.message || "Policy document saved successfully");
      clearFile();
      await fetchDocuments();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setUploading(false);
    }
  };

  const loadViewUrl = useCallback(
    async (row) => {
      const id = getDocId(row);
      if (!id) throw new Error("Document id is missing");
      if (!API_BASE_URL) throw new Error("Missing REACT_APP_API_URL in environment");
      if (!userId) throw new Error("Missing userId. Please login again.");

      const response = await axios.post(`${API_BASE_URL}`, {
        action: "byelaw.getViewUrl",
        userId,
        id
      });
      const data = response?.data;
      const ok = data?.success === true || data?.status === "success";
      const viewUrl = String(
        data?.viewUrl || data?.document?.viewUrl || data?.data?.viewUrl || ""
      ).trim();
      if (!ok || !viewUrl) {
        throw new Error(data?.message || "Could not open this document");
      }
      return viewUrl;
    },
    [API_BASE_URL, userId]
  );

  useEffect(() => {
    if (isAdmin || documents.length !== 1) {
      setInlineView(null);
      setInlineLoading(false);
      setInlineError("");
      return;
    }

    let cancelled = false;
    const row = documents[0];
    setInlineLoading(true);
    setInlineError("");
    setInlineView(null);

    loadViewUrl(row)
      .then((url) => {
        if (cancelled) return;
        setInlineView({
          name: getFileName(row) || "Bye-law",
          url
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setInlineError(
          error?.response?.data?.message || error?.message || "Could not open this document"
        );
      })
      .finally(() => {
        if (!cancelled) setInlineLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documents, isAdmin, loadViewUrl]);

  const saveFile = async (url, name) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Could not download this file");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = name || "document";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const handleRowDownload = async (row) => {
    const id = getDocId(row);
    const name = getFileName(row) || "document";
    if (!id) {
      toast.error("Document id is missing");
      return;
    }
    if (downloadingId) return;

    setDownloadingId(String(id));
    try {
      const viewUrl = await loadViewUrl(row);
      await saveFile(viewUrl, name);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Could not download this file"
      );
    } finally {
      setDownloadingId("");
    }
  };

  const openDocumentModal = async (row) => {
    const name = getFileName(row) || "Bye-law";
    setModal({ name, url: "", loading: true, error: "" });
    try {
      const url = await loadViewUrl(row);
      setModal({ name, url, loading: false, error: "" });
    } catch (error) {
      setModal({
        name,
        url: "",
        loading: false,
        error: error?.response?.data?.message || error?.message || "Could not open this document"
      });
    }
  };

  const closeModal = () => {
    setDownloading(false);
    setModal(null);
  };

  const handleDownload = async () => {
    const url = String(modal?.url || "").trim();
    const name = modal?.name || "document";
    if (!url || downloading) return;

    setDownloading(true);
    try {
      await saveFile(url, name);
    } catch (error) {
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = name;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        toast.error(error?.message || "Could not download this file");
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!isAdmin) return;
    const id = getDocId(row);
    if (!id) {
      toast.error("Document id is missing");
      return;
    }
    const name = getFileName(row) || "this document";
    if (!window.confirm(`Delete ${name}?`)) return;
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    setDeletingId(String(id));
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "byelaw.delete",
        userId,
        id
      });
      const data = response?.data;
      if (data?.success === true || data?.status === "success") {
        toast.success(data?.message || "Document deleted");
        await fetchDocuments();
      } else {
        toast.error(data?.message || "Failed to delete document");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setDeletingId("");
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="container-fluid">
          <h1 className="m-0 txt_lft">Document Management</h1>
          <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
            {isAdmin
              ? "Upload a Document. Owners and tenants can view it."
              : "Open your documents."}
          </p>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {isAdmin ? (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body">
                <form onSubmit={handleUpload}>
                  <div className="row align-items-end">
                    <div className="col-12 col-md-8">
                      <div className="form-group mb-md-0">
                        <label className="form-label">
                          Document <span className="required-star">*</span>
                        </label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="form-control"
                          accept={FILE_ACCEPT}
                          onChange={onFileChange}
                          disabled={uploading}
                        />
                        <small className="text-muted d-block mt-1">
                          PDF, Word, Excel, PNG, JPEG, JPG, or WEBP.
                        </small>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="d-flex justify-content-md-end" style={{ gap: 8 }}>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={uploading || !file}
                        >
                          {uploading ? "Uploading…" : "Upload"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={uploading || !file}
                          onClick={clearFile}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          ) : null}

          {isAdmin ? (
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div className="role-table-top">
                  <form onSubmit={(e) => e.preventDefault()} className="role-table-search-wrap">
                    <input
                      type="search"
                      className="form-control role-table-search"
                      placeholder="Search file name"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </form>
                  <div className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
                    Total: {filteredDocuments.length}
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>
                          <span className="role-th">Sl No.</span>
                        </th>
                        <th style={{ width: 220 }}>
                          <span className="role-th">File name</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Status</span>
                        </th>
                        <th style={{ width: 180 }}>
                          <span className="role-th">Created</span>
                        </th>
                        <th style={{ width: isAdmin ? 110 : 70 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {!listReady ? null : filteredDocuments.length ? (
                        filteredDocuments.map((row, idx) => {
                          const id = getDocId(row);
                          const status = String(row?.status || "active");
                          return (
                            <tr key={String(id || `row-${idx}`)}>
                              <td>{idx + 1}</td>
                              <td className="bylaw-file-name" title={getFileName(row) || ""}>
                                {getFileName(row) || "—"}
                              </td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    padding: "0.35rem 0.65rem",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    ...statusBadgeStyle(status)
                                  }}
                                >
                                  {status.toUpperCase()}
                                </span>
                              </td>
                              <td>{formatDateTime(row?.createdAt)}</td>
                              <td>
                                <div className="d-flex" style={{ gap: 8 }}>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-view"
                                    title="Download"
                                    disabled={downloadingId === String(id)}
                                    onClick={() => handleRowDownload(row)}
                                  >
                                    <i className="fa-solid fa-download" aria-hidden="true" />
                                  </button>
                                  {isAdmin ? (
                                    <button
                                      type="button"
                                      className="role-action-btn role-action-delete"
                                      title="Delete"
                                      disabled={deletingId === String(id)}
                                      onClick={() => handleDelete(row)}
                                    >
                                      <i className="fa-solid fa-trash" aria-hidden="true" />
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            {searchInput.trim()
                              ? "No documents match your search"
                              : "No bye-law documents"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          ) : !listReady ? null : documents.length === 0 ? (
            <div className="card">
              <div className="card-body text-muted">No bye-law documents</div>
            </div>
          ) : documents.length === 1 ? (
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center" style={{ gap: 10, marginBottom: 12 }}>
                  <i className={fileIconClass(getFileName(documents[0]))} style={{ color: "#0d5c66", fontSize: 22 }} />
                  <div>
                    <div style={{ fontWeight: 700 }}>
                      {getFileName(documents[0]) || "Bye-law"}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Updated {formatDateTime(documents[0]?.updatedAt)}
                    </div>
                  </div>
                </div>
                <div className="bylaw-viewer">
                  {inlineLoading ? (
                    <LoadingOverlay open variant="content" size={160} />
                  ) : inlineError ? (
                    <div className="text-danger">{inlineError}</div>
                  ) : inlineView?.url ? (
                    <DocumentPreview
                      name={inlineView.name}
                      url={inlineView.url}
                      frameClassName="bylaw-viewer-frame"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <form onSubmit={(e) => e.preventDefault()} className="bylaw-search">
                <input
                  type="search"
                  className="form-control role-table-search"
                  placeholder="Search file name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </form>
              {filteredDocuments.length ? (
            <div className="bylaw-grid">
              {filteredDocuments.map((row, idx) => {
                const id = getDocId(row);
                const name = getFileName(row) || "Bye-law";
                return (
                  <button
                    key={String(id || `doc-${idx}`)}
                    type="button"
                    className="bylaw-card"
                    onClick={() => openDocumentModal(row)}
                  >
                    <span className="bylaw-card-icon" aria-hidden="true">
                      <i className={fileIconClass(name)} />
                    </span>
                    <span className="bylaw-card-name">{name}</span>
                    <span className="bylaw-card-meta">
                      Updated {formatDateTime(row?.updatedAt)}
                    </span>
                  </button>
                );
              })}
            </div>
              ) : (
                <div className="card">
                  <div className="card-body text-muted">No documents match your search</div>
                </div>
              )}
            </div>
          )}

          {modal ? (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={modal.name}
              className="bylaw-modal-backdrop"
              onClick={closeModal}
            >
              <div className="bylaw-modal" onClick={(e) => e.stopPropagation()}>
                <div className="bylaw-modal-header">
                  <div className="bylaw-modal-title">{modal.name}</div>
                  <div className="bylaw-modal-actions">
                    <button
                      type="button"
                      className="btn btn-primary bylaw-modal-download"
                      disabled={!modal.url || modal.loading || downloading}
                      onClick={handleDownload}
                    >
                      <i className="fa-solid fa-download" aria-hidden="true" />
                      {downloading ? "Downloading…" : "Download"}
                    </button>
                    <button type="button" className="app-modal-close" onClick={closeModal} aria-label="Close">
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                </div>
                <div className="bylaw-modal-body">
                  {modal.loading ? (
                    <LoadingOverlay open variant="content" size={160} />
                  ) : modal.error ? (
                    <div className="text-danger">{modal.error}</div>
                  ) : (
                    <DocumentPreview
                      name={modal.name}
                      url={modal.url}
                      frameClassName="bylaw-modal-frame"
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DocumentManagement;
